<template>
  <section v-if="isLabExperienceUser" class="labs-experience">
    <h1 class="labs-experience__title">Lab Experience</h1>
    <div class="labs-experience__subtitle">
      Get hands-on practice with thousands of Cyber Security, Networking, and
      Cloud labs
    </div>
    <div class="labs-experience__tiles-wrapper">
      <router-link
        v-for="{
          link,
          background,
          icon,
          title,
          description,
          difficulty,
          total_labs,
        } in learningPathsTiles"
        :key="link"
        :to="link"
        :class="[
          'labs-experience__tile-link',
          `labs-experience__tile-link--${background}`,
        ]"
      >
        <div
          :class="[
            'labs-experience__tile-wrapper',
            `labs-experience__tile-wrapper--${background}`,
          ]"
        >
          <div class="labs-experience__tile-title">
            <component :is="icon" class="labs-experience__tile-icon" />
            {{ title }}
          </div>
          <div class="labs-experience__tile-details">
            <ActionLoader
              v-if="isFetching"
              :size="0.3"
              color="'#ffffff"
              name="line-spin-fade-loader"
              class="labs-experience__tile-details-spinner"
            />
            <div v-else class="labs-experience__tile-details-pill-wrapper">
              <div class="labs-experience__tile-details-pill"></div>
              <div class="labs-experience__tile-details-pill-border"></div>
              <PillBadge translucid>
                <strong>{{ `${total_labs} ` }}</strong>
                hands-on Labs
              </PillBadge>
            </div>
            <Difficulty
              v-if="!isFetching"
              :difficulty="difficulty"
              :alwaysVisible="true"
            />
          </div>
          <div class="labs-experience__tile-separator"></div>
          <div class="labs-experience__tile-description">
            {{ description }}
          </div>
        </div>
      </router-link>
    </div>
    <div class="labs-experience__labs-coll-wrapper">
      <div
        v-if="labExperienceCollection"
        class="labs-experience__labs-coll-title labs-experience__labs-coll-title--desktop"
      >
        Checkout our new collection of labs
      </div>
      <div
        v-if="labExperienceCollection"
        class="labs-experience__labs-coll-title labs-experience__labs-coll-title--mobile"
      >
        Lab Experience
      </div>
      <div
        v-if="labExperienceCollection"
        class="labs-experience__labs-coll-cards-wrapper"
      >
        <ActionLoader
          v-if="isFetching"
          name="line-spin-fade-loader"
          color="'#ffffff"
          class="labs-experience__labs-coll-spinner"
        />
        <template v-else>
          <router-link
            v-for="{
              id,
              title,
              isBeta,
              total_labs,
              difficulty,
              learningPath,
              description,
              link,
              icon,
            } in labsCollections"
            :key="id"
            v-model="labsCollections"
            :to="link"
            :class="[
              'labs-experience__labs-coll-link',
              'labs-experience__tile-link',
              `labs-experience__tile-link--${learningPath.slug}`,
            ]"
          >
            <div
              :class="[
                'labs-experience__labs-coll-card',
                `labs-experience__tile-wrapper--${learningPath.slug}`,
              ]"
            >
              <div class="labs-experience__tile-title">
                <component :is="icon" class="labs-experience__tile-icon" />
                {{ title }}
                <PillBadge
                  v-if="isBeta"
                  translucid
                  class="labs-experience__labs-coll-is-beta"
                >
                  BETA
                </PillBadge>
              </div>
              <div
                class="labs-experience__tile-details labs-experience__labs-coll-details"
              >
                <div
                  class="labs-experience__tile-details-pill-wrapper labs-experience__labs-coll-pill-wrapper"
                >
                  <div class="labs-experience__tile-details-pill"></div>
                  <div class="labs-experience__tile-details-pill-border"></div>
                  <PillBadge translucid>
                    <strong>{{ `${total_labs} ` }}</strong>
                    hands-on Labs
                  </PillBadge>
                </div>
                <Difficulty
                  v-if="!isFetching"
                  class="labs-experience__labs-coll-difficulty"
                  :difficulty="difficulty"
                  :alwaysVisible="true"
                />
                <div class="labs-experience__labs-coll-path-separator"></div>
                <div class="labs-experience__labs-coll-path">
                  <component
                    :is="learningPath.icon"
                    class="labs-experience__labs-coll-path-icon"
                  />
                  {{ learningPath.name }}
                </div>
              </div>
              <div
                class="labs-experience__tile-separator labs-experience__labs-coll-card-separator"
              ></div>
              <div class="labs-experience__tile-description">
                {{ description }}
              </div>
            </div>
          </router-link>
        </template>
      </div>
      <div class="labs-experience__coming-soon-wrapper">
        <ComingSoonIcon class="labs-experience__coming-soon-img" />
        <div class="labs-experience__coming-soon-title">
          Coming Soon: Enhanced Lab Experience!
        </div>
        <div class="labs-experience__coming-soon-text">
          <p>
            Our improved lab experience will make discovering new hands-on labs
            easier than ever.
          </p>
          <p>
            Get ready to dive into a vast library of engaging labs, making your
            learning journey more accessible and enjoyable. Stay tuned for the
            exciting updates that will take your skills to the next level.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CyberSecurity from '@/assets/images/learning-areas/noborders/cybersecurity.svg'
import Networking from '@/assets/images/learning-areas/noborders/networking.svg'
import Cloud from '@/assets/images/learning-areas/noborders/cloud.svg'
import ComingSoon from '@/assets/images/lab-experience/lab-experience-coming-soon.svg'
import PillBadge from '@/components/shared/PillBadge.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import Difficulty from '@/components/properties/Difficulty.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'LabExperienceRegular',
  components: {
    CloudIcon: { ...Cloud, name: 'CloudIcon' },
    CyberSecurityIcon: { ...CyberSecurity, name: 'CyberSecurityIcon' },
    NetworkingIcon: { ...Networking, name: 'NetworkingIcon' },
    ComingSoonIcon: { ...ComingSoon, name: 'ComingSoon' },
    PillBadge,
    ActionLoader,
    Difficulty,
  },
  data() {
    return {
      isFetching: false,
    }
  },
  computed: {
    ...mapGetters({
      isLabExperienceUser: 'auth/isLabExperienceUser',
      labExperienceCollection: 'featureFlags/labExperienceCollection',
    }),
    learningPathsTiles() {
      const {
        SPECIAL_LEARNING_PATHS: {
          SECURITY_LAB_EXPERIENCE,
          NETWORKING_LAB_EXPERIENCE,
          CLOUD_LAB_EXPERIENCE,
        },
      } = CONSTANTS
      return [
        {
          id: SECURITY_LAB_EXPERIENCE,
          background: 'cyber-security',
          icon: 'CyberSecurityIcon',
          title: 'Cyber Security Labs',
          description: `This path offers unique, real-world lab scenarios on topics spanning from recon, exploitation, post-exploitation, data exfiltration, web applications, traffic analysis, CVEs, network components, infrastructure attacks, privilege escalation, forensics, firmware analysis, reversing, secure coding, IoT networks, Metasploit, Python for infosec and many others.`,
          link: `/path/${SECURITY_LAB_EXPERIENCE}`,
        },
        {
          id: NETWORKING_LAB_EXPERIENCE,
          background: 'networking',
          icon: 'NetworkingIcon',
          title: 'Networking Labs',
          description: `This path offers unique, real-world lab scenarios on topics across the full Networking spectrum. Hundreds of new labs are currently in-development and will be added to this list as they're available.`,
          link: `/path/${NETWORKING_LAB_EXPERIENCE}`,
        },
        {
          id: CLOUD_LAB_EXPERIENCE,
          background: 'cloud',
          icon: 'CloudIcon',
          title: 'Cloud Labs',
          description: `This path offers unique, real-world lab scenarios on topics across the Cloud spectrum. Hundreds of new labs are currently in-development and will be added to this list as they're available.`,
          link: `/path/${CLOUD_LAB_EXPERIENCE}`,
        },
      ]
    },
    labsCollections() {
      const {
        LABS_COLLECTIONS: { SECURE_CODING },
      } = CONSTANTS
      return [
        {
          id: SECURE_CODING,
        },
      ]
    },
  },
  async created() {
    if (this.isLabExperienceUser) {
      const promises = []
      const promisesCollections = []
      for (let i = 0; i < this.learningPathsTiles.length; i++) {
        promises.push(this.getLearningPathById(this.learningPathsTiles[i].id))
      }
      for (let i = 0; i < this.labsCollections.length; i++) {
        promisesCollections.push(this.getCourseById(this.labsCollections[i].id))
      }
      this.isFetching = true
      await Promise.all(promises).then(values => {
        for (let i = 0; i < values.length; i++) {
          this.learningPathsTiles[i].total_labs = values[i].summary.total_labs
          this.learningPathsTiles[i].difficulty = values[i].difficulty
        }
      })
      await Promise.all(promisesCollections).then(values => {
        for (let i = 0; i < values.length; i++) {
          this.labsCollections[i].title = values[i].name.replace(' (Beta)', '')
          this.labsCollections[i].isBeta = values[i].slug.indexOf('beta') > -1
          this.labsCollections[i].total_labs = values[i].summary.total_labs
          this.labsCollections[i].difficulty = values[i].difficulty
          this.labsCollections[i].link = `/collection/${values[i].id}`
          this.labsCollections[i].description = this.setCollectionDescription(
            values[i].id
          )
          this.labsCollections[i].learningPath = this.setCollectionLP(
            values[i].id
          )
        }
      })
      this.isFetching = false
    } else {
      this.$router.push({
        name: 'home',
      })
    }
  },
  methods: {
    ...mapActions({
      getLearningPathById: 'learningPaths/getLearningPathById',
      getCourseById: 'courses/getCourseById',
    }),
    setCollectionDescription(collectionId) {
      const {
        LABS_COLLECTIONS: { SECURE_CODING },
      } = CONSTANTS
      let description = ''
      switch (collectionId) {
        case SECURE_CODING:
          description = `Designed for aspiring ethical hackers, security professionals, and enthusiasts, 
            this lab collection offers hands-on labs that challenge your skills in the art of
            exploitation and post-exploitation techniques.`
          break
      }
      return description
    },
    setCollectionLP(collectionId) {
      const {
        LEARNING_AREAS,
        LABS_COLLECTIONS: { SECURE_CODING },
      } = CONSTANTS
      let lp = ''
      switch (collectionId) {
        case SECURE_CODING:
          lp = LEARNING_AREAS.PRIMARY['Cyber Security']
          lp.icon = `${lp.name.replace(/ +/g, '')}Icon`
          break
      }
      return lp
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-experience {
  color: var(--white);
  font-family: var(--font-medium);
  background-image: url('../../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  min-height: 100vh;
  text-align: center;
  padding: 120px 20px;
  @include desktop {
    padding: 125px 25px 150px 25px;
  }
  &__title {
    font-weight: 450;
    font-size: rem(32px);
    line-height: 38px;
    @include desktop {
      font-weight: 700;
      font-size: rem(40px);
      line-height: 52px;
    }
  }
  &__subtitle {
    margin: 0 auto 60px auto;
    font-weight: 500;
    font-size: rem(18px);
    line-height: 24px;
    @include mobile {
      width: 70%;
    }
    @include desktop {
      width: 50%;
      font-weight: 450;
      font-size: rem(24px);
      line-height: 32px;
    }
  }
  &__tiles-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    flex-flow: column;
    @include desktop {
      flex-flow: row;
    }
  }
  &__tile {
    &-wrapper {
      width: 100%;
      border-radius: 20px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-flow: column;
      padding: 40px;
      @include desktop {
        height: 320px;
        width: 400px;
      }
      &--cloud {
        background: rgb(25, 50, 160);
        background: linear-gradient(
          180deg,
          rgba(25, 50, 160) 20%,
          rgba(22, 20, 30, 1) 50%
        );
      }
      &--cyber-security {
        background: rgb(50, 0, 110);
        background: linear-gradient(
          180deg,
          rgba(50, 0, 110, 1) 20%,
          rgba(22, 20, 30, 1) 50%
        );
      }
      &--networking {
        background: rgb(125, 40, 10);
        background: linear-gradient(
          180deg,
          rgba(125, 40, 10, 1) 20%,
          rgba(22, 20, 30, 1) 50%
        );
      }
    }
    &-link {
      width: 100%;
      padding: 1px;
      border-radius: 20px;
      @include desktop {
        width: unset;
      }
      &:hover {
        filter: drop-shadow(0px 4px 10px rgba(255, 255, 255, 0.4));
      }
      &--cloud {
        background: rgb(60, 70, 180);
        background: linear-gradient(
          160deg,
          rgba(60, 70, 180) 20%,
          rgba(174, 174, 178, 1) 50%,
          rgba(17, 17, 17, 1) 77%
        );
      }
      &--cyber-security {
        background: rgb(187, 133, 255);
        background: linear-gradient(
          160deg,
          rgba(187, 133, 255, 1) 20%,
          rgba(174, 174, 178, 1) 50%,
          rgba(17, 17, 17, 1) 77%
        );
      }
      &--networking {
        background: rgb(150, 80, 60);
        background: linear-gradient(
          160deg,
          rgba(150, 80, 60) 20%,
          rgba(174, 174, 178, 1) 50%,
          rgba(17, 17, 17, 1) 77%
        );
      }
    }
    &-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: var(--white);
      font-weight: 700;
      font-size: rem(20px);
      line-height: rem(26px);
      @include mobile {
        font-size: rem(30px);
        line-height: rem(32px);
      }
    }
    &-details {
      width: 100%;
      margin-top: 20px;
      display: flex;
      gap: 20px;
      height: 50px;
      &-spinner {
        padding-bottom: 4px;
      }
      &-pill {
        &-wrapper {
          position: relative;
          top: -39px;
        }
        background: var(--glass-solid-mid-gray);
        padding: 1px;
        border-radius: 20px;
        height: 26px;
        position: relative;
        top: 51px;
        opacity: 0.3;
        &-border {
          position: relative;
          top: 25px;
          border: grey solid 1px;
          opacity: 1;
          height: 26px;
          border-radius: 20px;
        }
      }
      .pill {
        white-space: nowrap;
        height: 24px;
        position: relative;
        z-index: 1;
        text-transform: uppercase;
      }
      .difficulty__label {
        text-transform: uppercase;
      }
    }
    &-separator {
      margin: 5px 20px 20px 20px;
      background: var(--glass-solid-mid-gray);
      width: 100%;
      padding: 0.5px;
    }
    &-description {
      color: var(--bright-gray);
      font-weight: 450;
      font-size: rem(12px);
      line-height: rem(14px);
      text-align: left;
    }
    &-icon {
      margin-right: 5px;
    }
  }
  &__labs-coll {
    &-wrapper {
      padding-top: 50px;
      max-width: 1250px;
      margin: auto;
      text-align: left;
      @include mobile {
        padding-top: 70px;
      }
    }
    &-spinner {
      text-align: center;
    }
    &-pill-wrapper {
      height: 26px;
      top: -60px;
      width: fit-content;
      @include mobile {
        margin-right: unset;
        top: -52px;
      }
    }
    &-link {
      display: block;
    }
    &-title {
      font-weight: 500;
      font-size: rem(18px);
      line-height: 24px;
      padding-bottom: 20px;
      @include desktop {
        font-weight: 450;
        font-size: rem(24px);
        line-height: 32px;
      }
      &--mobile {
        display: block;
        @include desktop {
          display: none;
        }
      }
      &--desktop {
        display: none;
        @include desktop {
          display: block;
        }
      }
    }
    &-card {
      width: 100%;
      padding: 40px;
      border-radius: 20px;
      &-separator {
        margin: 10px 0 20px 0;
      }
    }
    &-difficulty {
      width: unset !important;
      grid-area: 2/1/3/2;
      @include mobile {
        grid-area: unset;
      }
    }
    &-path {
      color: var(--white);
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      text-transform: uppercase;
      display: flex;
      align-items: center;
      grid-area: 2/3/3/3;
      white-space: nowrap;
      margin-bottom: 2px;
      &-icon {
        margin-right: 5px;
        width: 15px;
      }
      &-separator {
        width: 1px;
        height: 30px;
        margin-top: 10px;
        background: var(--glass-solid-mid-gray);
        grid-area: 2/2/3/3;
        @include mobile {
          grid-area: unset;
        }
      }
    }
    &-is-beta {
      margin-left: 10px;
      padding: 9px 14px !important;
    }
    &-details {
      display: grid;
      gap: 0 10px;
      margin-bottom: 35px;
      flex-wrap: wrap;
      align-items: self-end;
      @include mobile {
        display: flex;
        gap: 20px;
        margin-top: 0;
        margin-bottom: 0;
        align-content: center;
      }
    }
  }
  &__coming-soon {
    &-wrapper {
      text-align: center;
      padding-top: 60px;
      max-width: 900px;
      margin: auto;
    }
    &-img {
      width: 200px;
      height: 150px;
    }
    &-title {
      font-size: rem(18px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(24px);
      padding-bottom: 10px;
    }
    &-text {
      color: var(--light-blue-gray, #afb0bf);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px);
      p {
        margin-bottom: 0;
      }
    }
  }
}
</style>
